import React, { useEffect, useMemo, useRef, useState } from "react";
import ToolLayout from "./tools/ToolLayout";

export default function PropertyReportPage() {
  const [address, setAddress] = useState("");
  const [report, setReport] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [googlePlacesFailed, setGooglePlacesFailed] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  // Street View fields
  const [streetviewImageUrl, setStreetviewImageUrl] = useState(null);
  const [streetviewStatus, setStreetviewStatus] = useState(null);
  const [streetviewError, setStreetviewError] = useState(null);
  const [hideStreetview, setHideStreetview] = useState(false);

  const addressInputRef = useRef(null);
  const placeAutocompleteContainerRef = useRef(null);
  const placeAutocompleteElementRef = useRef(null);

  // =========================
  // GOOGLE AUTH FAILURE HANDLER
  // =========================
  useEffect(() => {
    const previousAuthFailure = window.gm_authFailure;

    window.gm_authFailure = () => {
      console.error("Google Maps authentication failed");
      setGooglePlacesFailed(true);
      setGoogleReady(false);
      setStatus(
        "Google address autocomplete is unavailable. You can still type your address manually."
      );
    };

    return () => {
      window.gm_authFailure = previousAuthFailure;
    };
  }, []);

  // =========================
  // LOAD GOOGLE SCRIPT + PLACES LIB
  // =========================
  useEffect(() => {
    let cancelled = false;

    const markUnavailable = () => {
      if (cancelled) return;
      setGooglePlacesFailed(true);
      setGoogleReady(false);
      setStatus(
        "Google address autocomplete is unavailable. You can still type your address manually."
      );
    };

    const loadPlacesLibrary = async () => {

        console.log("google.maps:", window.google?.maps);
        console.log("importLibrary exists:", !!window.google?.maps?.importLibrary);

        try {
        if (!window.google?.maps?.importLibrary) {
          markUnavailable();
          return;
        }

        const { PlaceAutocompleteElement } =
          await window.google.maps.importLibrary("places");

        console.log("PlaceAutocompleteElement:", PlaceAutocompleteElement);

        if (cancelled) return;

        if (!PlaceAutocompleteElement) {
          markUnavailable();
          return;
        }

        setGoogleReady(true);
      } catch (err) {
        console.error("Failed to initialize Places library", err);
        markUnavailable();
      }
    };

    const existingScript = document.querySelector(
      'script[data-google-places="1"]'
    );

    if (existingScript) {
      const handleLoad = () => loadPlacesLibrary();
      const handleError = () => markUnavailable();

      existingScript.addEventListener("load", handleLoad);
      existingScript.addEventListener("error", handleError);

      if (existingScript.getAttribute("data-loaded") === "true") {
        loadPlacesLibrary();
      }

      return () => {
        cancelled = true;
        existingScript.removeEventListener("load", handleLoad);
        existingScript.removeEventListener("error", handleError);
      };
    }

    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
    console.log("Google key present:", !!apiKey);

    if (!apiKey) {
      console.error("Missing Google Maps API key");
      markUnavailable();
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&v=weekly&libraries=places`;
    script.async = true;
    script.defer = true;
    script.setAttribute("data-google-places", "1");

    script.onload = () => {
      script.setAttribute("data-loaded", "true");
      loadPlacesLibrary();
    };

    script.onerror = () => {
      console.error("Failed to load Google script");
      markUnavailable();
    };

    document.head.appendChild(script);

    return () => {
      cancelled = true;
    };
  }, []);

  // =========================
  // INIT AUTOCOMPLETE ELEMENT
  // =========================
  useEffect(() => {
    if (!googleReady) return;
    if (!placeAutocompleteContainerRef.current) return;
    if (placeAutocompleteElementRef.current) return;

    let element;

    const init = async () => {
      const { PlaceAutocompleteElement } =
        await window.google.maps.importLibrary("places");

      element = new PlaceAutocompleteElement();

      element.setAttribute("placeholder", "Start typing your address...");
      element.style.display = "block";
      element.style.width = "100%";
      element.style.marginBottom = "12px";
      element.style.backgroundColor = "#ffffff";
      element.style.color = "#000000";

      const handlePlaceSelect = async (event) => {
        const prediction =
          event?.placePrediction || event?.detail?.placePrediction;

        if (!prediction?.toPlace) return;

        const place = prediction.toPlace();

        await place.fetchFields({ fields: ["formattedAddress"] });

        const formattedAddress = place.formattedAddress?.trim();
        if (!formattedAddress) return;

        setAddress(formattedAddress);
      };

      element.addEventListener("gmp-select", handlePlaceSelect);

      placeAutocompleteContainerRef.current.innerHTML = "";
      placeAutocompleteContainerRef.current.appendChild(element);

      placeAutocompleteElementRef.current = element;
    };

    init();

    return () => {
      if (placeAutocompleteElementRef.current) {
        placeAutocompleteElementRef.current.remove();
      }
      placeAutocompleteElementRef.current = null;
    };
  }, [googleReady]);

  // =========================
  // GENERATE REPORT
  // =========================
  async function handleGenerate(addressOverride) {
    const resolvedAddress = (addressOverride || address || "").trim();
    if (!resolvedAddress) return;

    setReport("");
    setStatus("");
    setLoading(true);
    setCollapsed({});
    setStreetviewImageUrl(null);
    setStreetviewStatus(null);
    setStreetviewError(null);
    setHideStreetview(false);

    try {
      const response = await fetch(
        `https://api.home-helper.com/api/property-report?address=${encodeURIComponent(
          resolvedAddress
        )}`
      );

      if (!response.ok || !response.body) {
        throw new Error("Failed to generate property report");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";

        for (let part of parts) {
          if (!part.startsWith("data: ")) continue;

          try {
            const json = JSON.parse(part.replace("data: ", ""));

            if (json.type === "status") setStatus(json.message);
            if (json.type === "content") setReport((prev) => prev + json.content);
            if (json.type === "error") setStatus("Error: " + json.message);
            if (json.type === "done") setLoading(false);

            // Street View fields (if present in any chunk)
            if (json.streetviewImageUrl !== undefined) setStreetviewImageUrl(json.streetviewImageUrl);
            if (json.streetviewStatus !== undefined) setStreetviewStatus(json.streetviewStatus);
            if (json.streetviewError !== undefined) setStreetviewError(json.streetviewError);
          } catch (err) {
            console.error("SSE parse error:", err);
          }
        }
      }
    } catch (err) {
      console.error("Request failed:", err);
      setStatus("Error generating report");
    } finally {
      setLoading(false);
    }
  }

  // =========================
  // PARSE SECTIONS
  // =========================
  const sections = useMemo(() => {
    if (!report) return [];

    const container = document.createElement("div");
    container.innerHTML = report;

    const parsed = [];
    let current = null;

    container.childNodes.forEach((node) => {
      if (node.nodeType === 1 && node.tagName === "H3") {
        if (current) parsed.push(current);
        current = { title: node.innerText, content: "" };
      } else if (current) {
        current.content += node.outerHTML || node.textContent || "";
      }
    });

    if (current) parsed.push(current);

    return parsed;
  }, [report]);

  const headerHtml = useMemo(() => {
    return report.match(/<h2>.*?<\/h2>/)?.[0] || "";
  }, [report]);

  const getIcon = (title) => {
    const t = title.toLowerCase();
    if (t.includes("summary")) return "🏠";
    if (t.includes("strength")) return "💪";
    if (t.includes("issue")) return "⚠️";
    if (t.includes("upgrade")) return "💡";
    if (t.includes("cost")) return "💰";
    if (t.includes("roi")) return "📈";
    return "📄";
  };

  const toggleCollapse = (title) => {
    setCollapsed((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  return (
    <ToolLayout>
      <div style={{ padding: "9px", maxWidth: "730px", margin: "0 auto" }}>
        <h1>Home Report Generator</h1>

        <p
          style={{
            marginTop: "8px",
            marginBottom: "16px",
            color: "#4b5563",
            lineHeight: 1.5,
          }}
        >
          Enter your home&apos;s address to generate a personalized property
          report with a summary, key strengths and issues, upgrade ideas, and
          cost and ROI insights based on your home and neighborhood.
        </p>

        {googleReady ? (
          <div ref={placeAutocompleteContainerRef} style={{ backgroundColor: "#ffffff" }} />
        ) : (
          <input
            ref={addressInputRef}
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Start typing your address..."
            style={{
              width: "100%",
              padding: "12px",
              marginBottom: "12px",
              fontSize: "16px",
              backgroundColor: "#ffffff",
              color: "#000000",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
            }}
          />
        )}

        {googlePlacesFailed && (
          <div style={{ marginTop: 8, color: "#666" }}>
            Autocomplete unavailable. You can still type manually.
          </div>
        )}

        <button
          onClick={() => handleGenerate()}
          disabled={!address.trim() || loading}
          style={{ padding: "12px 20px", fontSize: "16px" }}
        >
          {loading ? "Generating..." : "Generate Report"}
        </button>

        <p>{status}</p>

        {/* Report Title */}
        <div dangerouslySetInnerHTML={{ __html: headerHtml }} />

        {/* Street View Image (below title, above summary) */}
        {streetviewStatus === "success" && streetviewImageUrl && !hideStreetview && (
          <div style={{ margin: "24px 0 16px 0", textAlign: "center" }}>
            <img
              src={streetviewImageUrl}
              alt={address ? `Street view of ${address}` : "Street view image"}
              style={{
                width: "100%",
                maxWidth: 640,
                height: "auto",
                borderRadius: 12,
                boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
                marginBottom: 6,
                background: "#f3f4f6"
              }}
              onError={() => setHideStreetview(true)}
              loading="lazy"
            />
            <div style={{ fontSize: 13, color: "#888", marginTop: 2 }}>
              Street View image for this property
            </div>
          </div>
        )}

        <div>
          {sections.map((section, index) => {
            const isCollapsed = collapsed[section.title];

            return (
              <div key={index} style={{ marginBottom: 16 }}>
                <div onClick={() => toggleCollapse(section.title)}>
                  {getIcon(section.title)} {section.title}
                </div>
                {!isCollapsed && (
                  <div
                    dangerouslySetInnerHTML={{ __html: section.content }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </ToolLayout>
  );
}